import React, { useState, useEffect } from 'react';
import { Menu, X, Zap } from 'lucide-react';

const LOGO_URL = "/assets/logo.png";

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Stories', href: '#episodes' },
  { label: 'Sneak Peeks', href: '#sneak-peeks' },
  { label: 'Progress', href: '#progress' },
  { label: 'Buy Episodes', href: '#buy' },
  { label: 'Donate', href: '#donate' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black border-b border-white/5 shadow-lg shadow-black/50'
          : 'bg-black/90'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Shunya Gatha"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-2 ring-neonBlue/30 group-hover:ring-neonBlue/60 transition-all"
            />
            <div>
              <span className="font-heading font-bold text-lg sm:text-xl text-white tracking-wide">
                Shunya Gatha
              </span>
              <span className="hidden sm:block text-xs text-neonGold/70 font-hindi">शून्य गाथा</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className="px-3 py-2 text-sm font-medium text-gray-400 hover:text-neonBlue transition-colors relative group"
              >
                {link.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-neonBlue group-hover:w-full transition-all duration-300" />
              </a>
            ))}
            <a
              href="/admin"
              data-testid="nav-admin"
              className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-neonGold transition-colors"
            >
              Admin
            </a>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://t.me/the_warrior_SunyaGatha"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-telegram-btn"
              className="flex items-center gap-2 bg-[#2AABEE] text-white font-semibold px-5 py-2.5 rounded-full hover:bg-[#229ED9] hover:shadow-[0_0_20px_rgba(42,171,238,0.5)] transition-all text-sm"
            >
              <Zap size={16} />
              Join Telegram
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-gray-400 hover:text-neonBlue transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black border-t border-white/5"
        >
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className="block px-4 py-3 text-sm font-medium text-gray-300 hover:text-neonBlue hover:bg-white/5 rounded-lg transition-all"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="/admin"
              className="block px-4 py-3 text-sm font-medium text-gray-500 hover:text-neonGold hover:bg-white/5 rounded-lg transition-all"
              onClick={() => setMobileOpen(false)}
            >
              Admin Panel
            </a>
            <a
              href="https://t.me/the_warrior_SunyaGatha"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-3 text-center bg-[#2AABEE] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#229ED9] transition-all text-sm"
            >
              Join Telegram
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
